import React, { useState, useEffect } from 'react';
import { Label } from '../inputs/label';
import PhoneInput from '../inputs/PhoneInput';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../inputs/select';
import { getRolesForSelect } from '../../../data/roles/politicalRoles';

export default function ContactInfoModal({ isOpen, onClose, email, initialPhone, initialRole, onSubmit, delayMs = 1000 }) {
  const [role, setRole] = useState(initialRole || '');
  const [otherRole, setOtherRole] = useState('');
  const [phone, setPhone] = useState(initialPhone || { e164: '', national: '', country: 'US' });
  const [submitting, setSubmitting] = useState(false);
  const [delayedOpen, setDelayedOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const isValidPhone = (() => {
    const digits = String(phone?.national || '').replace(/[^0-9]/g, '');
    return digits.length >= 10; // allow >=10 so extra keypresses don't flip disabled
  })();

  useEffect(() => {
    setRole(initialRole || '');
    setPhone(initialPhone || { e164: '', national: '', country: 'US' });
  }, [initialRole, initialPhone]);

  useEffect(() => {
    if (isOpen) {
      // Delay mount
      const t = setTimeout(() => {
        setDelayedOpen(true);
        // start fade-in
        requestAnimationFrame(() => setVisible(true));
      }, delayMs);
      return () => {
        clearTimeout(t);
        setDelayedOpen(false);
        setVisible(false);
      };
    } else {
      setDelayedOpen(false);
      setVisible(false);
    }
  }, [isOpen, delayMs]);

  if (!delayedOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const digits = String(phone?.national || '').replace(/[^0-9]/g, '');
    if (digits.length !== 10) {
      setPhoneError('Enter a valid 10-digit US phone number.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        role: role === 'Other' ? otherRole : role,
        phone
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 dark:bg-black/60 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}>
      <div className={`w-[95%] max-w-[720px] rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 transition-transform duration-300 ${visible ? 'scale-100' : 'scale-95'}`}>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Confirm your contact information</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">We use this to help with support and account communications.</p>
        <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
          <div>
            <Label>Email</Label>
            <input
              type="email"
              value={email || ''}
              disabled
              className="block w-full rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 py-2 px-3.5 text-gray-600 dark:text-gray-300 shadow-sm ring-1 ring-inset ring-gray-200 dark:ring-gray-700 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Problem with your email? Contact support at <a className="underline" href="mailto:support@robocent.com">support@robocent.com</a>.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <PhoneInput
                id="ci-phone"
                name="ci-phone"
                label="Phone number"
                required
                defaultCountry="US"
                onChange={(val) => {
                  setPhone(val);
                  if (phoneError) setPhoneError('');
                }}
                value={phone?.e164}
              />
              {phoneError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{phoneError}</p>
              )}
            </div>
            <div>
              <Label>Your role</Label>
              <Select id="ci-role" value={role} onValueChange={setRole}>
                <SelectTrigger className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {getRolesForSelect().map(r => (
                    <SelectItem key={r.value} value={r.label}>{r.label}</SelectItem>
                  ))}
                  {!getRolesForSelect().some(r => r.label === 'Other') && (
                    <SelectItem value="Other">Other</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {role === 'Other' && (
                <div className="mt-3">
                  <Label>Please specify</Label>
                  <input
                    type="text"
                    onChange={(e) => setOtherRole(e.target.value)}
                    className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700">Close</button>
          <button type="submit" disabled={submitting || !isValidPhone} className={`px-4 py-2 rounded-md text-white font-semibold shadow-md ${submitting || !isValidPhone ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'}`}>{submitting ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


